import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Legend,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { theme } from "../../../../constants/StyledComponents";
import { NO_DATA_CHART_LABEL } from "../../../../constants/Dictionary";
import {
  LegendItem,
  StyledDevelopmentChart,
  StyledLegend,
} from "./DevelopmentChart.styled";
import { LabelFormatter } from "recharts/types/component/Label";
import { FontWeightEnum } from "../../../../types/AppTypes";
import { useDimensions } from "../../../../hooks/useDimensions.hook";
import {
  INTERNAL_GRADE_LABEL_TO_KEY,
  INTERNAL_GRADE_LIST,
  INTERNAL_GRADE_META,
  QUADRANT_LABELS,
} from "../../../../constants/AppConstants";
import { DevelopmentChartQuadrantStats } from "../../../../types/api/potentialMatrix.type";
import { Loader } from "../../../../components/core/Loader/Loader";
import { StyledMessageContainer } from "../../PotentialMatrixV3.styled";
import { EmptyState } from "../../../../components/EmptyState/EmptyState";
import MatrixDevelopmenetImage from ".././../../../images/empty_matrix.png";
import { LegendLabelDot } from "../../../../components/LegendLabelDot/LegendLabelDot";
import { ScreenEnum } from "../../../../types/api/collaborators.type";

type DevelopmentChartProps = {
  potentialMatrixStatsData: DevelopmentChartQuadrantStats[];
  loadingPotentialMatrixStats?: boolean;
  showPercentage: boolean;
};

export const DevelopmentChart = ({
  potentialMatrixStatsData,
  loadingPotentialMatrixStats,
  showPercentage = false,
}: DevelopmentChartProps) => {
  const { width } = useDimensions();
  const isCompactLayout = width <= theme.breakpoints.XL;
  const data = potentialMatrixStatsData ?? [];
  const hasData = data.some((row) => row.internalGrades.length > 0);

  const chartData = [...data].map((row) => {
    const grades = Object.fromEntries(
      row.internalGrades.map((item) => [
        INTERNAL_GRADE_LABEL_TO_KEY[item.internalGrade] ?? item.internalGrade,
        showPercentage ? item.percentage : item.count,
      ]),
    );

    return { screen: row.screen, ...grades };
  });

  const maxCount = Math.max(
    ...data.flatMap((row) => row.internalGrades.map((item) => item.count)),
  );

  //adatta lo step dell'asse in base alla grandezza dei dati
  const getYAxisStep = (max: number): number => {
    if (max <= 10) return 1;
    return 5;
  };

  const Y_AXIS_STEP = showPercentage ? 10 : getYAxisStep(maxCount);

  const yAxisMax = showPercentage
    ? 100
    : Math.floor(maxCount / Y_AXIS_STEP) * Y_AXIS_STEP + Y_AXIS_STEP * 2;

  const yAxisStep = showPercentage ? 10 : Y_AXIS_STEP;

  const yAxisTicks = (() => {
    const ticks = [];
    for (let value = 0; value <= yAxisMax; value += yAxisStep) {
      ticks.push(value);
    }
    return ticks;
  })();

  const formatLabel: LabelFormatter = (value) =>
    showPercentage ? `${value}%` : value;

  const renderLegend = () => (
    <StyledLegend>
      {INTERNAL_GRADE_LIST.map((internalGrade) => (
        <LegendItem key={internalGrade}>
          <LegendLabelDot
            label={INTERNAL_GRADE_META[internalGrade].label}
            color={INTERNAL_GRADE_META[internalGrade].color}
            fontWeight={FontWeightEnum.REGULAR}
            variant="3"
          />
        </LegendItem>
      ))}
    </StyledLegend>
  );

  const splitLabelInTwoLines = (label: string): [string, string] => {
    const words = label.split(" ");
    const midPoint = Math.ceil(words.length / 2);
    return [
      words.slice(0, midPoint).join(" "),
      words.slice(midPoint).join(" "),
    ];
  };

  return (
    <StyledDevelopmentChart>
      <div className="chart-body">
        {loadingPotentialMatrixStats ? (
          <div className="chart-loading-container">
            <Loader loading={true} size={32} />
          </div>
        ) : hasData ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              accessibilityLayer
              data={chartData}
              margin={{ top: 16, right: 30, left: 0, bottom: 24 }}
              barSize={20}
              barGap={4}
            >
              <CartesianGrid vertical={false} stroke={theme.colors.purple_2} />
              <XAxis
                dataKey="screen"
                tickLine={false}
                axisLine={false}
                tick={({ x, y, payload }) => {
                  const screenKey = payload.value as ScreenEnum;
                  return (
                    <g transform={`translate(${x},${y})`}>
                      <text
                        x={0}
                        y={16}
                        textAnchor="middle"
                        fontSize={theme.variables.fs_24}
                        fill={theme.colors.black}
                        fontWeight={theme.variables.fw_semiBold}
                      >
                        {payload.value}
                      </text>
                      {isCompactLayout ? (
                        <text
                          x={0}
                          y={30}
                          textAnchor="middle"
                          fontSize={theme.variables.fs_10}
                          fill={theme.colors.black}
                          fontWeight={theme.variables.fw_medium}
                        >
                          {splitLabelInTwoLines(QUADRANT_LABELS[screenKey]).map(
                            (line, index) => (
                              <tspan key={line} x={0} dy={index === 0 ? 0 : 12}>
                                {line}
                              </tspan>
                            ),
                          )}
                        </text>
                      ) : (
                        <text
                          x={0}
                          y={32}
                          textAnchor="middle"
                          fontSize={theme.variables.fs_12}
                          fill={theme.colors.black}
                          fontWeight={theme.variables.fw_medium}
                        >
                          {QUADRANT_LABELS[screenKey]}
                        </text>
                      )}
                    </g>
                  );
                }}
              />
              <YAxis
                width={25}
                domain={[0, yAxisMax]}
                ticks={yAxisTicks}
                allowDecimals={false}
                tickLine={false}
                axisLine={false}
                fontSize={theme.variables.fs_12}
                fontWeight={theme.variables.fw_Regular}
                tick={{ fill: theme.colors.black }}
              />
              <Legend
                content={renderLegend}
                align="left"
                wrapperStyle={{ position: "absolute", bottom: 0, left: 0 }}
              />
              {INTERNAL_GRADE_LIST.map((internalGrade) => {
                return (
                  <Bar
                    key={internalGrade}
                    dataKey={internalGrade}
                    name={internalGrade}
                    fill={INTERNAL_GRADE_META[internalGrade].color}
                    radius={[4, 4, 4, 4]}
                    isAnimationActive={false}
                  >
                    <LabelList
                      dataKey={internalGrade}
                      position="top"
                      fontSize={theme.variables.fs_10}
                      fontWeight={theme.variables.fw_semiBold}
                      fill={theme.colors.black}
                      formatter={formatLabel}
                    />
                  </Bar>
                );
              })}
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <StyledMessageContainer>
            <EmptyState
              imageSrc={MatrixDevelopmenetImage}
              label={NO_DATA_CHART_LABEL}
            />
          </StyledMessageContainer>
        )}
      </div>
    </StyledDevelopmentChart>
  );
};
